import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { usePageTitle } from "../../hooks/usePageTitle";
import {
  getQuizThemes,
  createQuizTheme,
  updateQuizTheme,
  deleteQuizTheme,
  getThemeQuizzes,
  setThemeQuizOrder,
  type QuizTheme,
  type ThemeQuiz,
} from "../../services/quiz-themes";
import AdminPageHeader, { AdminButton } from "../../components/admin/AdminPageHeader";

export default function QuizThemesPage() {
  const { t } = useTranslation();
  usePageTitle(t("quizThemes.title", "Teme Quiz"));

  const [themes, setThemes] = useState<QuizTheme[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTheme, setEditingTheme] = useState<QuizTheme | null>(null);

  const [formData, setFormData] = useState({ name: "", title: "" });

  // Reorder-quizzes modal state
  const [reorderTheme, setReorderTheme] = useState<QuizTheme | null>(null);
  const [themeQuizzes, setThemeQuizzes] = useState<ThemeQuiz[]>([]);
  const [reorderLoading, setReorderLoading] = useState(false);
  const [savingOrder, setSavingOrder] = useState(false);

  useEffect(() => {
    loadThemes();
  }, []);

  const openReorder = async (theme: QuizTheme) => {
    setReorderTheme(theme);
    setReorderLoading(true);
    try {
      setThemeQuizzes(await getThemeQuizzes(theme.id));
    } catch {
      setThemeQuizzes([]);
    } finally {
      setReorderLoading(false);
    }
  };

  const moveQuiz = (index: number, dir: -1 | 1) => {
    setThemeQuizzes((prev) => {
      const next = [...prev];
      const j = index + dir;
      if (j < 0 || j >= next.length) return prev;
      [next[index], next[j]] = [next[j], next[index]];
      return next;
    });
  };

  const saveOrder = async () => {
    if (!reorderTheme) return;
    setSavingOrder(true);
    try {
      await setThemeQuizOrder(reorderTheme.id, themeQuizzes.map((q) => q.id));
      setReorderTheme(null);
      setThemeQuizzes([]);
    } catch {
      alert("Nu am putut salva ordinea.");
    } finally {
      setSavingOrder(false);
    }
  };

  const loadThemes = async () => {
    try {
      setLoading(true);
      const data = await getQuizThemes();
      setThemes(data);
    } catch (error) {
      console.error("Failed to load quiz themes:", error);
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingTheme(null);
    setFormData({ name: "", title: "" });
    setShowModal(true);
  };

  const openEditModal = (theme: QuizTheme) => {
    setEditingTheme(theme);
    setFormData({ name: theme.name, title: theme.title });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingTheme(null);
    setFormData({ name: "", title: "" });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.title) return;

    try {
      if (editingTheme) {
        await updateQuizTheme(editingTheme.id, {
          name: formData.name,
          title: formData.title,
        });
      } else {
        await createQuizTheme({
          name: formData.name,
          title: formData.title,
        });
      }
      await loadThemes();
      closeModal();
    } catch (error: any) {
      console.error("Failed to save theme:", error);
      alert(error.response?.data?.error || "Nu s-a putut salva tema");
    }
  };

  const handleDelete = async (theme: QuizTheme) => {
    const quizCount = theme._count?.quizzes || 0;
    const msg = quizCount > 0
      ? `Tema "${theme.title}" are ${quizCount} quiz-uri asociate. Quiz-urile nu vor fi șterse, doar dezasociate. Continui?`
      : `Ștergi tema "${theme.title}"?`;
    if (!window.confirm(msg)) return;

    try {
      await deleteQuizTheme(theme.id);
      await loadThemes();
    } catch (error: any) {
      console.error("Failed to delete theme:", error);
      alert(error.response?.data?.error || "Nu s-a putut șterge tema");
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 p-6">
      <div className="mx-auto max-w-[1500px]">
        {/* Header */}
        <AdminPageHeader
          eyebrow="Content & Education"
          title={t("quizThemes.title", "Teme Quiz")}
          subtitle={t("quizThemes.description", "Grupează quiz-uri pe teme. O temă poate conține quiz-uri din categorii diferite.")}
          icon={
            <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
            </svg>
          }
          actions={
            <AdminButton
              onClick={openCreateModal}
              icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>}
            >
              {t("quizThemes.addTheme", "Adaugă temă")}
            </AdminButton>
          }
        />

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="flex flex-col items-center gap-4">
              <div className="w-10 h-10 border-4 border-violet-200 border-t-violet-600 rounded-full animate-spin"></div>
              <p className="text-slate-500">{t("common.loading", "Se încarcă...")}</p>
            </div>
          </div>
        ) : (
          <>
            {/* Stats */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 mb-8 inline-flex items-center gap-4">
              <div className="p-3 bg-violet-50 rounded-xl">
                <svg className="w-6 h-6 text-violet-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                </svg>
              </div>
              <div>
                <p className="text-sm text-slate-500">{t("quizThemes.totalThemes", "Total teme")}</p>
                <p className="text-2xl font-bold text-slate-800">{themes.length}</p>
              </div>
            </div>

            {/* Themes List */}
            {themes.length === 0 ? (
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8 text-center">
                <div className="p-4 bg-slate-100 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                  <svg className="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                  </svg>
                </div>
                <p className="text-slate-500">{t("quizThemes.noThemes", "Nicio temă creată")}</p>
                <button onClick={openCreateModal} className="mt-4 text-violet-600 hover:text-violet-700 font-medium text-sm">
                  + {t("quizThemes.addFirst", "Adaugă prima temă")}
                </button>
              </div>
            ) : (
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                <table className="w-full">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200">
                      <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                        {t("quizThemes.nameColumn", "Nume (slug)")}
                      </th>
                      <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                        {t("quizThemes.titleColumn", "Titlu")}
                      </th>
                      <th className="text-center px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                        {t("quizThemes.quizzesColumn", "Quiz-uri")}
                      </th>
                      <th className="text-right px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                        {t("common.actions", "Acțiuni")}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {themes.map((theme) => (
                      <tr key={theme.id} className="hover:bg-slate-50 transition-colors">
                        <td className="px-6 py-4 text-sm font-mono text-slate-500">{theme.name}</td>
                        <td className="px-6 py-4 text-sm font-semibold text-slate-800">{theme.title}</td>
                        <td className="px-6 py-4 text-sm text-center">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-violet-50 text-violet-700">
                            {theme._count?.quizzes || 0}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <div className="flex justify-end gap-2">
                            <button
                              onClick={() => openReorder(theme)}
                              disabled={(theme._count?.quizzes || 0) < 2}
                              title={t("quizThemes.reorder", "Ordonează quiz-uri")}
                              className="px-3 py-1.5 bg-slate-50 hover:bg-slate-100 text-slate-600 rounded-lg transition-colors text-sm font-medium inline-flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h7" />
                              </svg>
                              {t("quizThemes.order", "Ordine")}
                            </button>
                            <button
                              onClick={() => openEditModal(theme)}
                              className="px-3 py-1.5 bg-slate-50 hover:bg-slate-100 text-slate-600 rounded-lg transition-colors text-sm font-medium inline-flex items-center gap-1.5"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                              {t("common.edit", "Editează")}
                            </button>
                            <button
                              onClick={() => handleDelete(theme)}
                              className="px-3 py-1.5 bg-red-50 hover:bg-red-100 text-red-500 rounded-lg transition-colors text-sm font-medium inline-flex items-center gap-1.5"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {/* Create/Edit Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-violet-50 rounded-lg">
                  <svg className="w-5 h-5 text-violet-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-slate-800">
                  {editingTheme ? t("quizThemes.editTheme", "Editează tema") : t("quizThemes.createTheme", "Creează temă")}
                </h3>
              </div>

              <form onSubmit={handleSubmit}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    {t("quizThemes.nameLabel", "Nume (slug)")} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value.toLowerCase().replace(/\s+/g, "_") })
                    }
                    placeholder="ex: educatie_sportiva"
                    className="w-full px-4 py-2.5 bg-white border border-slate-300 rounded-xl text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-violet-500 transition-all font-mono"
                    required
                  />
                  <p className="text-xs text-slate-400 mt-1">Litere mici, underscore. Folosit ca identificator.</p>
                </div>

                <div className="mb-6">
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    {t("quizThemes.titleLabel", "Titlu afișat")} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="ex: Educație Sportivă"
                    className="w-full px-4 py-2.5 bg-white border border-slate-300 rounded-xl text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-violet-500 transition-all"
                    required
                  />
                </div>

                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="flex-1 px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-medium transition-colors"
                  >
                    {t("common.cancel", "Anulează")}
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2.5 bg-violet-600 hover:bg-violet-700 text-white rounded-xl font-medium transition-colors shadow-sm"
                  >
                    {editingTheme ? t("common.save", "Salvează") : t("common.create", "Creează")}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Reorder quizzes modal */}
        {reorderTheme && (
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full p-6 max-h-[85vh] flex flex-col">
              <div className="flex items-center gap-3 mb-1">
                <div className="p-2 bg-violet-50 rounded-lg">
                  <svg className="w-5 h-5 text-violet-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h7" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-slate-800">
                  {t("quizThemes.orderTitle", "Ordinea quiz-urilor")}
                </h3>
              </div>
              <p className="text-sm text-slate-500 mb-4">
                {reorderTheme.title} — {t("quizThemes.orderHint", "ordinea în care apar quiz-urile din această temă")}
              </p>

              <div className="flex-1 overflow-y-auto -mx-1 px-1">
                {reorderLoading ? (
                  <p className="text-sm text-slate-500 py-6 text-center">{t("common.loading", "Se încarcă...")}</p>
                ) : themeQuizzes.length === 0 ? (
                  <p className="text-sm text-slate-500 py-6 text-center">
                    {t("quizThemes.noQuizzes", "Această temă nu are quiz-uri atașate.")}
                  </p>
                ) : (
                  <div className="space-y-2">
                    {themeQuizzes.map((q, i) => (
                      <div
                        key={q.id}
                        className="flex items-center gap-3 p-3 bg-slate-50 border border-slate-200 rounded-xl"
                      >
                        <span className="w-6 text-center text-sm font-bold text-slate-400">{i + 1}</span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-slate-800 truncate">{q.title}</p>
                          <p className="text-xs text-slate-400">
                            {q.category || "—"}
                            {!q.is_active && ` · ${t("quizThemes.inactive", "inactiv")}`}
                          </p>
                        </div>
                        <div className="flex flex-col gap-1">
                          <button
                            onClick={() => moveQuiz(i, -1)}
                            disabled={i === 0}
                            className="w-7 h-6 rounded bg-white border border-slate-200 text-slate-600 disabled:opacity-30 hover:bg-slate-100"
                          >
                            ↑
                          </button>
                          <button
                            onClick={() => moveQuiz(i, 1)}
                            disabled={i === themeQuizzes.length - 1}
                            className="w-7 h-6 rounded bg-white border border-slate-200 text-slate-600 disabled:opacity-30 hover:bg-slate-100"
                          >
                            ↓
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex gap-3 mt-4">
                <button
                  type="button"
                  onClick={() => {
                    setReorderTheme(null);
                    setThemeQuizzes([]);
                  }}
                  className="flex-1 px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-medium transition-colors"
                >
                  {t("common.cancel", "Anulează")}
                </button>
                <button
                  type="button"
                  onClick={saveOrder}
                  disabled={savingOrder || themeQuizzes.length === 0}
                  className="flex-1 px-4 py-2.5 bg-violet-600 hover:bg-violet-700 text-white rounded-xl font-medium transition-colors shadow-sm disabled:opacity-50"
                >
                  {savingOrder ? t("common.saving", "Se salvează...") : t("common.save", "Salvează")}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
